import React, { useEffect, useState, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { RxCross2 } from "react-icons/rx";
import { RiArrowUpSLine } from 'react-icons/ri';
import { RxCaretSort } from 'react-icons/rx';
import ModalHeading from './modal-heading';
import Loader from './loader';
import Pagination from './pagination';
import { getAuditLogs } from '../../services/audit-service';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { camelCaseToWords } from '../../utils/structure-words-utils';

const AuditLogModal = ({ isOpen, onClose, accountId, linkedType, linkedId }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilter, setGlobalFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sorting, setSorting] = useState([]);

    useEffect(() => {
        if (isOpen && accountId && linkedType && linkedId) {
            fetchAuditLogs();
        }
    }, [isOpen, accountId, linkedType, linkedId]);

    const fetchAuditLogs = async () => {
        try {
            setLoading(true);
            console.log("Audit request : ", accountId, linkedType, linkedId);
            const response = await getAuditLogs(accountId, linkedType, linkedId);
            console.log("Audit response : ", response);
            if (response.success) {
                setData(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching audit logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'createdOn',
                header: 'Time',
                cell: (info) => formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a'),
            },
            {
                accessorKey: 'action',
                header: 'Action',
                cell: (info) => <span className="capitalize">{info.getValue()}</span>,
            },
            {
                accessorKey: 'field',
                header: 'Field',
                cell: (info) => camelCaseToWords(info.getValue()) || '----',
            },
            {
                accessorKey: 'oldValue',
                header: 'Old Value',
                cell: (info) => {
                    const val = info.getValue();
                    return val === null || val === undefined ? '----' : String(val);
                },
            },
            {
                accessorKey: 'newValue',
                header: 'New Value',
                cell: (info) => {
                    const val = info.getValue();
                    return val === null || val === undefined ? '----' : String(val);
                },
            },
            {
                accessorKey: 'user.name',
                header: 'User',
                cell: (info) => info.getValue() || '----',
            },
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
    });

    if (!isOpen) return null;

    return (
        <div className="flex flex-col gap-4">
            <div className="px-4 border-b">
                <div className="pb-2 relative">
                    <ModalHeading className="text-left">Audit Logs</ModalHeading>
                    <button
                        className="hover:scale-105 absolute -top-2 -right-2 bg-white border p-2 rounded-full"
                        onClick={onClose}
                    >
                        <RxCross2 className="text-xl" />
                    </button>
                </div>
            </div>

            <div className="px-4">
                <div className="mb-4">
                    <input
                        type="text"
                        value={globalFilter ?? ''}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="Search audit logs..."
                    />
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    {loading ? (
                        <div className="py-20">
                            <Loader />
                        </div>
                    ) : (
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-100">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                className="px-4 py-3 text-left font-medium text-gray-700 capitalize cursor-pointer select-none"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                <div className="flex items-center gap-1">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <span className="text-gray-400">
                                                            {header.column.getIsSorted() === 'asc' ? (
                                                                <RiArrowUpSLine className="text-base" />
                                                            ) : header.column.getIsSorted() === 'desc' ? (
                                                                <RiArrowUpSLine className="text-base rotate-180" />
                                                            ) : (
                                                                <RxCaretSort className="text-lg" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {table.getRowModel().rows.length > 0 ? (
                                    table.getRowModel().rows
                                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                        .map((row) => (
                                            <tr key={row.id} className="hover:bg-gray-50">
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id} className="px-4 py-3 max-w-[220px]  text-gray-600">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="px-4 py-10 text-center text-gray-500">
                                            No audit logs found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && data.length > 0 && (
                    <div className="mt-4">
                        <Pagination
                            currentPage={currentPage}
                            totalItems={table.getFilteredRowModel().rows.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setCurrentPage}
                            onItemsPerPageChange={(count) => {
                                setItemsPerPage(count);
                                setCurrentPage(1);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuditLogModal;
